import type { FlexProps } from '@chakra-ui/react';
import { Box, Center, Flex, Show, Span, Text } from '@chakra-ui/react';
import type { Msg } from '@halo/chat-sdk';
import {
  FileTypeIcon,
  serializePayload,
  ForwardPreviewIcon,
  StickerTypeIcon,
} from '@halo/ui-chat-react';
import { isStickerMessage, MessageStatus, MsgPayloadType } from '@halo/chat-sdk';

export interface ForwardComposerPreviewProps extends FlexProps {
  message?: Msg | null;
}

function ForwardComposerPreview({ message, ...props }: ForwardComposerPreviewProps) {
  const { t } = useTranslation();
  const preventDefault = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const replyInfo = useMemo(() => {
    switch (message?.payload_type) {
      case MsgPayloadType.FILE:
        return `[${t('chat.file')}] ${message?.payload?.name}`;
      case MsgPayloadType.STICKER:
        return `[${t('chat.sticker')}]`;
      case MsgPayloadType.IMAGE:
        return `[${t('chat.photo')}]`;
      case MsgPayloadType.LUCKY_BOX:
        return `[${t('chat.luckyBox')}]`;
      default:
        switch (message?.status) {
          case MessageStatus.FailedEncrypt:
            // return <Span color="fg.error">Failed to encrypt message</Span>;
            return serializePayload.deserializeContent(message?.payload);
          case MessageStatus.FailedDecrypt:
            return <Span color="fg.error">{t('tips.failedToDecryptMessage')}</Span>;
          default:
            return serializePayload.deserializeContent(message?.payload);
        }
    }
  }, [message, t]);

  const isFile = useMemo(() => {
    return (
      message?.payload_type === MsgPayloadType.FILE ||
      message?.payload_type === MsgPayloadType.IMAGE
    );
  }, [message?.payload_type]);

  const isSticker = useMemo(() => isStickerMessage(message), [message]);

  return (
    <Flex
      className="MessageComposerReplyPreview"
      alignItems="center"
      gap="1"
      justify="space-between"
      pt="1"
      onClick={preventDefault}
      onMouseDown={preventDefault}
      overflow="hidden"
      {...props}
    >
      <Show when={message}>
        <Center color="brand.100" pb="2">
          <ForwardPreviewIcon />
        </Center>
        <Box flex="1" pb="1.5" overflow="hidden">
          <Flex
            alignItems="center"
            justify="space-between"
            gap="2"
            pl="2"
            borderLeft="2px solid var(--colors-brand-100)"
            overflow="hidden"
          >
            <Flex flex="1" alignItems="center" gap="1" overflow="hidden">
              {isSticker && (
                <StickerTypeIcon
                  sticker_id={message?.payload?.id}
                  category={message?.payload?.stickerSetInfo?.category}
                />
              )}
              {isFile && (
                <FileTypeIcon
                  style={{ borderRadius: 1 }}
                  extType={message?.payload?.ext}
                  src={message?.payload?.thumbnail}
                />
              )}
              <Flex flex="1" direction="column" gap="1" overflow="hidden">
                <Text
                  fontSize="xs"
                  color="brand.100"
                  fontWeight="medium"
                  lineHeight="normal"
                  truncate
                >
                  {t('chat.forwardMessage')}
                </Text>
                <Text
                  fontSize="xs"
                  color="fg.muted"
                  lineHeight="normal"
                  truncate
                  fontWeight="medium"
                >
                  {replyInfo}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Box>
      </Show>
    </Flex>
  );
}

export { ForwardComposerPreview };
